import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface CopilotActiveContext {
  incidentId?: string;
  incidentReference?: string;
  deviceId?: string;
}

export interface CopilotThreadSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
}

export type CopilotActivityKind = "status" | "tool" | "decision";

export interface CopilotActivityEntry {
  id: string;
  kind: CopilotActivityKind;
  text: string;
  ts: number;
}

export interface CopilotState {
  /** Controls global visibility of the side drawer. */
  isOpen: boolean;
  /** Current pipeline processing state streamed from the backend. */
  currentStatusText: string;
  /** Identifies the target entity currently tracked by the copilot. */
  activeContext: CopilotActiveContext;
  /**
   * A prompt queued by an external trigger (e.g. the incident "Analyze"
   * action) to be auto-sent once the drawer/runtime has mounted. Consumed
   * and cleared by the CopilotPanel.
   */
  pendingPrompt: string | null;
  /** Always-visible timeline of workflow steps for transparency. */
  activityLog: CopilotActivityEntry[];
  /** Active persisted thread bound to the runtime. */
  activeThreadId?: string;
  /**
   * `current_node` of the persisted workflow state for the active thread
   * (e.g. PENDING_EDGE_ACK, MCAP_UPLOADED). Hydrated when a conversation is
   * opened from history; authoritative fallback for the MCAP approval card
   * after the live SSE stream is gone (page refresh / thread revisit).
   */
  activeWorkflowNode?: string;
  /** Sidebar thread list hydrated from the API. */
  threads: CopilotThreadSummary[];
  /** Loading state for thread list hydration. */
  threadsLoading: boolean;
}

const initialState: CopilotState = {
  isOpen: false,
  currentStatusText: "IDLE",
  activeContext: {},
  pendingPrompt: null,
  activityLog: [],
  activeThreadId: undefined,
  activeWorkflowNode: undefined,
  threads: [],
  threadsLoading: false,
};

export const copilotSlice = createSlice({
  name: "copilot",
  initialState,
  reducers: {
    /** Flip the drawer open/closed. */
    toggleDrawer(state) {
      state.isOpen = !state.isOpen;
    },
    /** Explicitly set the drawer open/closed. */
    setDrawerOpen(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload;
    },
    /** Update the current pipeline status string. */
    setStatusText(state, action: PayloadAction<string>) {
      state.currentStatusText = action.payload;
    },
    /** Replace the active context target identifiers. */
    setActiveContext(state, action: PayloadAction<CopilotActiveContext>) {
      state.activeContext = action.payload;
    },
    /** Clear the active context target identifiers. */
    flushActiveContext(state) {
      state.activeContext = {};
    },
    /**
     * Combined action used by monitoring table "Analyze Incident" buttons:
     * sets the target context and immediately opens the drawer.
     */
    openWithContext(state, action: PayloadAction<CopilotActiveContext>) {
      state.isOpen = true;
      state.activeContext = action.payload;
    },
    /**
     * Fired by the incident "Analyze" menu item: opens the drawer, targets the
     * incident/device, and queues a prompt for the runtime to auto-send.
     */
    requestAnalysis(
      state,
      action: PayloadAction<{
        incidentId: string;
        incidentReference?: string;
        prompt?: string;
      }>,
    ) {
      const { incidentId, incidentReference, prompt } = action.payload;
      state.isOpen = true;
      state.activeContext = { incidentId, incidentReference };
      // External analyze triggers always start a fresh persisted conversation.
      state.activeThreadId = undefined;
      state.pendingPrompt =
        prompt ??
        "Analyze the marked incident: identify the root cause or next steps, and summarize the findings in a concise report.";
    },
    /** Clear the queued prompt once it has been dispatched to the runtime. */
    clearPendingPrompt(state) {
      state.pendingPrompt = null;
    },
    /** Append one timeline entry (bounded to keep memory stable). */
    pushActivity(
      state,
      action: PayloadAction<Omit<CopilotActivityEntry, "id" | "ts">>,
    ) {
      state.activityLog.push({
        id: `${Date.now()}-${state.activityLog.length}`,
        ts: Date.now(),
        ...action.payload,
      });
      if (state.activityLog.length > 120) {
        state.activityLog.splice(0, state.activityLog.length - 120);
      }
    },
    /** Clear timeline at the start of a new run. */
    clearActivity(state) {
      state.activityLog = [];
    },
    /** Bind the runtime to a persisted thread. */
    setActiveThreadId(state, action: PayloadAction<string | undefined>) {
      state.activeThreadId = action.payload;
    },
    /** Record the persisted workflow node for the active thread. */
    setActiveWorkflowNode(state, action: PayloadAction<string | undefined>) {
      state.activeWorkflowNode = action.payload;
    },
    /** Replace sidebar threads from API payload. */
    setThreads(state, action: PayloadAction<CopilotThreadSummary[]>) {
      state.threads = action.payload;
    },
    /** Update/insert one thread summary in-place. */
    upsertThread(state, action: PayloadAction<CopilotThreadSummary>) {
      const idx = state.threads.findIndex((t) => t.id === action.payload.id);
      if (idx >= 0) {
        state.threads[idx] = action.payload;
      } else {
        state.threads.unshift(action.payload);
      }
      state.threads.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    },
    /** Update just the title of an existing thread (e.g. after regeneration). */
    renameThread(state, action: PayloadAction<{ id: string; title: string }>) {
      const thread = state.threads.find((t) => t.id === action.payload.id);
      if (thread) {
        thread.title = action.payload.title;
      }
    },
    /** Remove a thread from sidebar state. */
    removeThread(state, action: PayloadAction<string>) {
      state.threads = state.threads.filter((t) => t.id !== action.payload);
      if (state.activeThreadId === action.payload) {
        state.activeThreadId = undefined;
      }
    },
    /** Set async loading state for thread list fetches. */
    setThreadsLoading(state, action: PayloadAction<boolean>) {
      state.threadsLoading = action.payload;
    },
  },
});

export const {
  toggleDrawer,
  setDrawerOpen,
  setStatusText,
  setActiveContext,
  flushActiveContext,
  openWithContext,
  requestAnalysis,
  clearPendingPrompt,
  pushActivity,
  clearActivity,
  setActiveThreadId,
  setActiveWorkflowNode,
  setThreads,
  upsertThread,
  renameThread,
  removeThread,
  setThreadsLoading,
} = copilotSlice.actions;

export const copilotReducer = copilotSlice.reducer;
